import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LINE_ITEM_OMISE_FEE } from '../../util/types';

import css from './OrderBreakdown.module.css';
import { PAYMENT_METHODS } from '../../util/enums';
import { getEffectivePaymentMethod } from '../../transactions/transaction';

const { Money } = sdkTypes;

const safeLower = v => (typeof v === 'string' ? v.toLowerCase() : '');

// Validate the assumption that the fee exists and amount is zero or positive.
const isValidCommission = lineItem => {
  return lineItem && lineItem.lineTotal instanceof Money && Number(lineItem.lineTotal.amount) >= 0;
};

// ✅ Normalize method coming from UI / storage / backend
const normalizePaymentMethod = method => {
  if (!method) return null;

  const m = String(method)
    .trim()
    .toLowerCase();

  if (
    m === 'card' ||
    m === 'credit_card' ||
    m === 'line-item/card' ||
    m === safeLower(PAYMENT_METHODS.CARD)
  ) {
    return PAYMENT_METHODS.CARD;
  }

  // handle promptpay typos too
  if (m === 'promptpay' || m === 'promtpay' || m === safeLower(PAYMENT_METHODS.PROMPTPAY)) {
    return PAYMENT_METHODS.PROMPTPAY;
  }

  if (
    m === 'mobile_banking' ||
    m === 'mobile-banking' ||
    m === 'mobilebanking' ||
    m === safeLower(PAYMENT_METHODS.MOBILE_BANKING)
  ) {
    return PAYMENT_METHODS.MOBILE_BANKING;
  }

  return null;
};

// ✅ If refresh happens, selectedPaymentMethod might be empty.
// Try to infer method from tx/protectedData if available, else default to CARD silently.
const inferMethod = (selectedPaymentMethod, txOrProtectedData) => {
  const normalized = normalizePaymentMethod(selectedPaymentMethod);
  if (normalized) return normalized;

  const fromTx =
    getEffectivePaymentMethod(txOrProtectedData) ||
    txOrProtectedData?.attributes?.protectedData?.paymentMethod ||
    txOrProtectedData?.protectedData?.paymentMethod ||
    txOrProtectedData?.paymentMethod;

  const normalizedFromTx = normalizePaymentMethod(fromTx);
  if (normalizedFromTx) return normalizedFromTx;

  return PAYMENT_METHODS.CARD; // ✅ safe default
};

const LineItemOmiseFeeMaybe = props => {
  const { lineItems, isCustomer, intl, selectedPaymentMethod, transaction, protectedData } = props;

  const items = Array.isArray(lineItems) ? lineItems : [];
  const customerOmiseFeeLineItem = items.find(
    item => item.code === LINE_ITEM_OMISE_FEE && !item.reversal
  );

  if (!isCustomer || !customerOmiseFeeLineItem) return null;

  if (!isValidCommission(customerOmiseFeeLineItem)) {
    console.error('invalid omise fee line item:', customerOmiseFeeLineItem);
    return null; // ✅ don’t crash the whole checkout
  }

  const method = inferMethod(selectedPaymentMethod, transaction || protectedData);

  // Display text (optional)
  let vatFee = '7% (VAT)';
  let omsieTransactionFee = '3.65% (MDR)';

  if (method === PAYMENT_METHODS.PROMPTPAY) omsieTransactionFee = '1.65% (MDR)';
  if (method === PAYMENT_METHODS.MOBILE_BANKING) omsieTransactionFee = '฿10.00 fixed fee';

  const feeMoney = customerOmiseFeeLineItem?.lineTotal;
  const formattedFee = feeMoney ? formatMoney(intl, feeMoney) : null;

  return (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="OrderBreakdown.omiseProcessingFee" />
        {/* tooltip later: omsieTransactionFee + vatFee */}
      </span>
      <span className={css.itemValue}>{formattedFee}</span>
    </div>
  );
};

export default LineItemOmiseFeeMaybe;
